/**
 * Directory search for a list page. All search logic lives in
 * src/utils/directorySearch.ts; swapping to a search service later
 * (docs/directory-plan.md section 9) changes this one file.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HoursEntry, HoursMode, Listing, OpeningHoursResult } from '../types';
import { getCategory } from '../config/categories';
import { resolveOpeningHours } from '../utils/openingHours';
import { addDaysToDate } from '../utils/israelTime';
import { LatLng } from '../utils/geo';
import {
  DirectoryFilters,
  EMPTY_FILTERS,
  LabelLookup,
  directoryParamsFromFilters,
  parseDirectoryParams,
  searchDirectory,
} from '../utils/directorySearch';
import { useIsraelNow } from './useIsraelNow';
import { useZmanimGetter } from './useZmanimGetter';
import { useShiurNext } from './useShiurim';

/** Opening hours for every listing that uses them (not shuls), keyed by listing id. */
export function useOpeningHoursMap(listings: Listing[]): Record<string, OpeningHoursResult> {
  const now = useIsraelNow();
  // Yesterday (for hours past midnight) through next week (for "next opening").
  const getZmanim = useZmanimGetter(addDaysToDate(now.date, -1), 15);

  return useMemo(() => {
    const out: Record<string, OpeningHoursResult> = {};
    for (const listing of listings) {
      if (listing.category === 'shul' || !getCategory(listing.category).usesHours) continue;
      out[listing.id] = resolveOpeningHours(listing, getZmanim, now);
    }
    return out;
  }, [listings, getZmanim, now]);
}

/** Opening hours for hours that are still being edited (the admin's live "This week" preview). */
export function useOpeningHoursPreview(hours: HoursEntry[], hoursMode: HoursMode): OpeningHoursResult {
  const now = useIsraelNow();
  const getZmanim = useZmanimGetter(addDaysToDate(now.date, -1), 15);
  return useMemo(() => resolveOpeningHours({ hours, hoursMode }, getZmanim, now), [hours, hoursMode, getZmanim, now]);
}

/** URL params that belong to the page, not the filters; kept when filters change. */
const PAGE_PARAMS = ['view'];

function withPageParams(next: URLSearchParams, current: URLSearchParams): URLSearchParams {
  for (const key of PAGE_PARAMS) {
    const value = current.get(key);
    if (value) next.set(key, value);
  }
  return next;
}

/** `origin` = the user's location after tapping Near me: results are sorted nearest first. */
export function useDirectorySearch(listings: Listing[], origin?: LatLng | null) {
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseDirectoryParams(searchParams), [searchParams]);
  const hoursById = useOpeningHoursMap(listings);
  const { nextById: nextShiurById, isToday } = useShiurNext(listings);

  const labels = useMemo<LabelLookup>(() => {
    const tEn = i18n.getFixedT('en');
    const tHe = i18n.getFixedT('he');
    return (kind, key) => {
      const path = `directory.${kind === 'category' ? 'category' : 'sub'}.${key}`;
      return [tEn(path), tHe(path)];
    };
  }, [i18n]);

  const results = useMemo(
    () =>
      searchDirectory({
        listings,
        filters,
        labels,
        statusOf: (l) => hoursById[l.id]?.status,
        isHebrew,
        origin,
        shiurToday: isToday,
      }),
    [listings, filters, labels, hoursById, isHebrew, origin, isToday]
  );

  /** Change some filters. Typing in the search box replaces the history entry instead of adding one. */
  const updateFilters = useCallback(
    (change: Partial<DirectoryFilters>, options: { replace?: boolean } = {}) => {
      const next = { ...parseDirectoryParams(searchParams), ...change };
      setSearchParams(withPageParams(directoryParamsFromFilters(next), searchParams), { replace: options.replace });
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(withPageParams(directoryParamsFromFilters(EMPTY_FILTERS), searchParams));
  }, [searchParams, setSearchParams]);

  return { results, filters, updateFilters, clearFilters, hoursById, nextShiurById };
}
